import { calculateHaversineDistance } from '@/services/trackMetricsCalculationService'
import type { Segment, Waypoint } from '@/types/tour'

export type TrackGradientBucket = {
  id:
    | 'steepDownhill'
    | 'downhill'
    | 'gentleDownhill'
    | 'flat'
    | 'gentleUphill'
    | 'moderateUphill'
    | 'steepUphill'
  maxGradePercent: number
  color: string
}

export const GRADE_BUCKETS: TrackGradientBucket[] = [
  { id: 'steepDownhill', maxGradePercent: -10, color: '#1d4ed8' },
  { id: 'downhill', maxGradePercent: -6, color: '#2563eb' },
  { id: 'gentleDownhill', maxGradePercent: -3, color: '#38bdf8' },
  { id: 'flat', maxGradePercent: 3, color: '#22c55e' },
  { id: 'gentleUphill', maxGradePercent: 6, color: '#eab308' },
  { id: 'moderateUphill', maxGradePercent: 10, color: '#f97316' },
  { id: 'steepUphill', maxGradePercent: Number.POSITIVE_INFINITY, color: '#dc2626' },
]

export type TrackGradientFeatureProperties = {
  bucketId: TrackGradientBucket['id'] | 'fallback'
  color: string
}

export type TrackGradientFeature = GeoJSON.Feature<
  GeoJSON.LineString,
  TrackGradientFeatureProperties
>

const MIN_DISTANCE_METERS = 1

const isFiniteNumber = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value)

const getLegGradePercent = (start: Waypoint, end: Waypoint): number | null => {
  const distanceMeters = calculateHaversineDistance(start.lat, start.lon, end.lat, end.lon)
  if (!Number.isFinite(distanceMeters) || distanceMeters < MIN_DISTANCE_METERS) {
    return null
  }

  if (isFiniteNumber(start.ele) && isFiniteNumber(end.ele)) {
    return ((end.ele - start.ele) / distanceMeters) * 100
  }

  if (isFiniteNumber(end.grade)) return end.grade
  if (isFiniteNumber(start.grade)) return start.grade
  return null
}

export const resolveTrackGradientBucket = (gradePercent: number | null) => {
  if (gradePercent === null) {
    return GRADE_BUCKETS.find((bucket) => bucket.id === 'flat') ?? GRADE_BUCKETS[0]
  }

  return (
    GRADE_BUCKETS.find((bucket) => gradePercent <= bucket.maxGradePercent) ??
    GRADE_BUCKETS[GRADE_BUCKETS.length - 1]
  )
}

const buildFeature = (
  coordinates: [number, number][],
  color: string,
  bucketId: TrackGradientFeatureProperties['bucketId'],
): TrackGradientFeature => ({
  type: 'Feature',
  properties: {
    color,
    bucketId,
  },
  geometry: {
    type: 'LineString',
    coordinates,
  },
})

export const buildTrackGradientFeatureCollection = (
  segments: Segment[],
  fallbackColor: string = '#3b82f6',
): GeoJSON.FeatureCollection<GeoJSON.LineString, TrackGradientFeatureProperties> => {
  const features: TrackGradientFeature[] = []
  let activeCoordinates: [number, number][] | null = null
  let activeColor: string | null = null
  let activeBucketId: TrackGradientFeatureProperties['bucketId'] | null = null

  const flushActiveFeature = () => {
    if (!activeCoordinates || !activeColor || !activeBucketId || activeCoordinates.length < 2) {
      return
    }

    features.push(buildFeature(activeCoordinates, activeColor, activeBucketId))
  }

  for (const segment of segments) {
    for (let pointIndex = 1; pointIndex < segment.points.length; pointIndex += 1) {
      const start = segment.points[pointIndex - 1]
      const end = segment.points[pointIndex]
      const gradePercent = getLegGradePercent(start, end)
      const bucket = gradePercent === null ? null : resolveTrackGradientBucket(gradePercent)
      const color = bucket?.color ?? fallbackColor
      const bucketId = bucket?.id ?? 'fallback'
      const startCoordinate: [number, number] = [start.lon, start.lat]
      const endCoordinate: [number, number] = [end.lon, end.lat]
      const canExtendActiveFeature =
        activeCoordinates &&
        activeColor === color &&
        activeBucketId === bucketId &&
        activeCoordinates[activeCoordinates.length - 1][0] === startCoordinate[0] &&
        activeCoordinates[activeCoordinates.length - 1][1] === startCoordinate[1]

      if (canExtendActiveFeature && activeCoordinates) {
        activeCoordinates.push(endCoordinate)
        continue
      }

      flushActiveFeature()
      activeCoordinates = [startCoordinate, endCoordinate]
      activeColor = color
      activeBucketId = bucketId
    }
  }

  flushActiveFeature()

  return {
    type: 'FeatureCollection',
    features,
  }
}
